/**
 * OkChatModal — Native in-app OkChat modal with full REST API integration.
 *
 * Opens as a centered modal over the current page.
 * Uses OkChat REST API (via our backend proxy) to show real conversations.
 * Falls back to embedded OkChat iframe if the user has no OkChat account.
 *
 * Triggered via window.okChatOpen({ sellerId?, sellerNickname?, adTitle? })
 * or the floating MessageSquare button.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import {
    MessageSquare, X, Send, ArrowLeft, Search, Plus,
    RefreshCw, Wifi, WifiOff, ExternalLink, Users, Circle,
    Loader2,
} from 'lucide-react';

const ACCENT  = '#0288D1';
const OKCHAT_BASE = 'https://okchat.okgo.mx';

/* ── Token cache helpers (sessionStorage, 60-min TTL, scoped per user) ───── */
const TTL_MS = 60 * 60 * 1000; // 60 minutes

const cacheKey = (userId) => `okchat_session_${userId}`;

export function getCachedToken(userId) {
    if (!userId) return null;
    try {
        const raw = sessionStorage.getItem(cacheKey(userId));
        if (!raw) return null;
        const { token, base, exp } = JSON.parse(raw);
        if (Date.now() > exp) { sessionStorage.removeItem(cacheKey(userId)); return null; }
        return { token, base };
    } catch { return null; }
}

export function setCachedToken(userId, token, base) {
    if (!userId) return;
    try {
        sessionStorage.setItem(cacheKey(userId), JSON.stringify({ token, base, exp: Date.now() + TTL_MS }));
    } catch {}
}

export function clearCachedToken(userId) {
    try {
        if (userId) {
            sessionStorage.removeItem(cacheKey(userId));
        } else {
            // Clear all okchat session keys (e.g. on full logout when userId unknown)
            Object.keys(sessionStorage)
                .filter(k => k.startsWith('okchat_session_'))
                .forEach(k => sessionStorage.removeItem(k));
        }
    } catch {}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const fmt = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

const avatar = (name = '?') => (name || '?')[0].toUpperCase();

/* ── Sub-components ──────────────────────────────────────────────────────── */
function ConvList({ convs, activeId, onSelect, onNew, search, setSearch, loading }) {
    const filtered = convs.filter(c => {
        const n = (c.name || c.other_user?.nickname || c.participants?.[0]?.nickname || '').toLowerCase();
        return n.includes(search.toLowerCase());
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'hidden' }}>
            {/* Search */}
            <div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                <div style={{ position: 'relative' }}>
                    <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar conversación…"
                        style={{
                            width: '100%', border: '1px solid #e5e7eb', borderRadius: 8,
                            padding: '6px 10px 6px 28px', fontSize: 12, outline: 'none',
                            background: '#f9fafb', boxSizing: 'border-box',
                        }}
                    />
                </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                        <Loader2 size={22} style={{ display: 'block', margin: '0 auto 8px', animation: 'spin 1s linear infinite' }} />
                        <div style={{ fontSize: 12 }}>Cargando conversaciones…</div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{ padding: '40px 16px', textAlign: 'center', color: '#9ca3af' }}>
                        <Users size={28} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.4 }} />
                        <div style={{ fontSize: 12 }}>{search ? 'Sin resultados' : 'No tienes conversaciones aún'}</div>
                    </div>
                ) : filtered.map((c, i) => {
                    const name = c.name || c.other_user?.nickname || c.participants?.find(p => p.is_other)?.nickname || 'Usuario';
                    const last = c.last_message?.content || '';
                    const unread = c.unread_count || 0;
                    const isActive = c.id === activeId;
                    return (
                        <button
                            key={c.id || i}
                            onClick={() => onSelect(c)}
                            style={{
                                width: '100%', padding: '10px 12px', display: 'flex', alignItems: 'center',
                                gap: 10, border: 'none', borderBottom: '1px solid #f9fafb',
                                background: isActive ? '#e3f2fd' : '#fff', cursor: 'pointer',
                                textAlign: 'left', transition: 'background 0.1s',
                            }}
                            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
                        >
                            <div style={{
                                width: 38, height: 38, borderRadius: '50%',
                                background: isActive ? ACCENT : '#94a3b8',
                                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: 15, flexShrink: 0,
                            }}>
                                {avatar(name)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{name}</div>
                                {last && <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', marginTop: 1 }}>{last}</div>}
                            </div>
                            {unread > 0 && (
                                <div style={{ background: ACCENT, color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '2px 7px', flexShrink: 0 }}>{unread}</div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* New chat button */}
            <div style={{ padding: '10px 12px', borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
                <button
                    onClick={onNew}
                    style={{
                        width: '100%', background: ACCENT, color: '#fff', border: 'none',
                        borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                >
                    <Plus size={14} /> Nueva conversación
                </button>
            </div>
        </div>
    );
}

function ChatRoom({ conv, messages, myNickname, onBack, input, setInput, onSend, sending, loadingMsgs }) {
    const bottomRef = useRef(null);
    const inputRef  = useRef(null);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
    useEffect(() => { if (!sending) inputRef.current?.focus(); }, [sending, conv]);

    const name = conv.name || conv.other_user?.nickname || 'Usuario';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'hidden' }}>
            {/* Room header */}
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8, background: '#fff', flexShrink: 0 }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                    <ArrowLeft size={16} />
                </button>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: ACCENT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                    {avatar(name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
                    <div style={{ fontSize: 10, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Circle size={7} fill="#22c55e" color="#22c55e" /> En línea
                    </div>
                </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 4px', display: 'flex', flexDirection: 'column', gap: 6, background: '#f9fafb' }}>
                {loadingMsgs ? (
                    <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto 8px' }} />
                        <div style={{ fontSize: 12 }}>Cargando mensajes…</div>
                    </div>
                ) : messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 32 }}>
                        Inicia la conversación con {name}
                    </div>
                ) : messages.map((m, i) => {
                    const isOwn = m.sender?.nickname === myNickname || m.sender_nickname === myNickname || m.is_own;
                    return (
                        <div key={m.id || i} style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                            <div style={{
                                maxWidth: '78%', padding: '7px 10px',
                                borderRadius: isOwn ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                background: isOwn ? ACCENT : '#fff',
                                color: isOwn ? '#fff' : '#111827',
                                fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word',
                                boxShadow: !isOwn ? '0 1px 2px rgba(0,0,0,0.07)' : 'none',
                            }}>
                                <div>{m.content || m.text || m.message}</div>
                                <div style={{ fontSize: 10, opacity: 0.65, marginTop: 3, textAlign: 'right' }}>
                                    {fmt(m.created_at || m.ts || m.timestamp)}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {sending && (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', paddingLeft: 4 }}>
                        {[0, 1, 2].map(i => (
                            <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', display: 'inline-block', animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
                        ))}
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form onSubmit={onSend} style={{ padding: '8px 10px 10px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, background: '#fff', flexShrink: 0 }}>
                <input
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    disabled={sending}
                    placeholder="Escribe un mensaje…"
                    style={{
                        flex: 1, border: '1px solid #e5e7eb', borderRadius: 10,
                        padding: '7px 11px', fontSize: 13, outline: 'none',
                        background: '#f9fafb', boxSizing: 'border-box',
                    }}
                    onFocus={e => { e.target.style.borderColor = ACCENT; e.target.style.background = '#fff'; }}
                    onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; }}
                />
                <button
                    type="submit"
                    disabled={!input.trim() || sending}
                    style={{
                        background: input.trim() && !sending ? ACCENT : '#e5e7eb',
                        border: 'none', borderRadius: 10,
                        color: input.trim() && !sending ? '#fff' : '#9ca3af',
                        width: 36, height: 36,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: input.trim() && !sending ? 'pointer' : 'default',
                        transition: 'background 0.15s', flexShrink: 0,
                    }}
                >
                    <Send size={14} />
                </button>
            </form>
        </div>
    );
}

function IframeFallback({ onClose }) {
    const [loaded, setLoaded] = useState(false);
    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {!loaded && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8', flexDirection: 'column', gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 14, background: `linear-gradient(135deg, ${ACCENT}, #01579B)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MessageSquare size={22} color="#fff" />
                    </div>
                    <div style={{ fontSize: 14, color: '#6b7280' }}>Conectando con OkChat…</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {[0, 1, 2].map(i => (
                            <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: ACCENT, animation: `pulseDot 1.4s ${i * 0.2}s ease-in-out infinite`, opacity: 0.5 }} />
                        ))}
                    </div>
                </div>
            )}
            <iframe
                src={OKCHAT_BASE}
                title="OkChat"
                style={{ flex: 1, border: 'none', opacity: loaded ? 1 : 0, transition: 'opacity 0.3s', width: '100%', height: '100%' }}
                onLoad={() => setLoaded(true)}
                allow="camera; microphone; autoplay; clipboard-read; clipboard-write"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
            />
        </div>
    );
}

/* ── Main modal component ─────────────────────────────────────────────────── */
export default function OkChatModal({ open, onClose, initialSellerId, initialSellerNickname, onConvRead }) {
    const { user, role } = useAuth();

    // OkChat state
    const [status, setStatus]       = useState('idle'); // idle | loading | ready | no_account | error
    const [okchatToken, setToken]   = useState(null);
    const [okchatBase, setBase]     = useState(OKCHAT_BASE);

    // Conversation state
    const [convs, setConvs]         = useState([]);
    const [activeConv, setActiveConv] = useState(null);
    const [messages, setMessages]   = useState([]);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [loadingConvs, setLoadingConvs] = useState(false);
    const [input, setInput]         = useState('');
    const [sending, setSending]     = useState(false);
    const [search, setSearch]       = useState('');
    const [unreadTotal, setUnread]  = useState(0);

    // Authenticate with OkChat (uses sessionStorage cache scoped to this user, TTL 60 min)
    useEffect(() => {
        if (!open || role !== 'user') return;
        if (okchatToken) return; // already authenticated in this component instance

        const userId = user?._id || user?.id || user?.nickname;

        // Try the cache first — skip the round-trip if valid
        const cached = getCachedToken(userId);
        if (cached) {
            setToken(cached.token);
            setBase(cached.base || OKCHAT_BASE);
            setStatus('ready');
            return;
        }

        setStatus('loading');
        api.get('/okchat/auth')
            .then(r => {
                if (r.data?.token) {
                    const base = r.data.okchat_base_url || OKCHAT_BASE;
                    setCachedToken(userId, r.data.token, base);
                    setToken(r.data.token);
                    setBase(base);
                    setStatus('ready');
                } else {
                    setStatus('no_account');
                }
            })
            .catch(() => setStatus('no_account'));
    }, [open, role, okchatToken, user]);

    // Clear cache + reset state so next open re-authenticates
    const handleAuthExpired = useCallback(() => {
        const userId = user?._id || user?.id || user?.nickname;
        clearCachedToken(userId);
        setToken(null);
        setStatus('idle');
    }, [user]);

    // Load conversations when ready
    const loadConvs = useCallback(async () => {
        if (!okchatToken) return;
        setLoadingConvs(true);
        try {
            const r = await api.get('/okchat/conversations', { params: { okchat_token: okchatToken } });
            const list = r.data?.conversations || [];
            setConvs(list);
            setUnread(list.reduce((s, c) => s + (c.unread_count || 0), 0));
        } catch (err) {
            if (err?.response?.status === 401) { handleAuthExpired(); }
            else { setConvs([]); }
        } finally {
            setLoadingConvs(false);
        }
    }, [okchatToken, handleAuthExpired]);

    useEffect(() => {
        if (status === 'ready') loadConvs();
    }, [status, loadConvs]);

    // If opened with an initial seller — find or start their conversation
    useEffect(() => {
        if (!initialSellerNickname || !convs.length || activeConv) return;
        const found = convs.find(c => {
            const n = c.name || c.other_user?.nickname || '';
            return n.toLowerCase().includes(initialSellerNickname.toLowerCase());
        });
        if (found) openConv(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convs, initialSellerNickname]);

    const openConv = useCallback(async (conv) => {
        setActiveConv(conv);
        setMessages([]);
        setLoadingMsgs(true);

        // Optimistically clear the per-conversation unread badge in the sidebar
        // and notify the parent widget so the floating button badge updates immediately.
        const prevUnread = conv.unread_count || 0;
        if (prevUnread > 0) {
            setConvs(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
            setUnread(prev => Math.max(0, prev - prevUnread));
            onConvRead?.(prevUnread);
        }

        try {
            const r = await api.get(`/okchat/conversations/${conv.id}/messages`, {
                params: { okchat_token: okchatToken, limit: 50 },
            });
            setMessages(r.data?.messages || []);
            // Mark as read (fire-and-forget; optimistic update already applied above)
            api.post(`/okchat/conversations/${conv.id}/mark-read`, {}, {
                params: { okchat_token: okchatToken },
            }).catch(() => {});
        } catch (err) {
            if (err?.response?.status === 401) { handleAuthExpired(); }
            else { setMessages([]); }
        } finally {
            setLoadingMsgs(false);
        }
    }, [okchatToken, handleAuthExpired, onConvRead]);

    const sendMsg = useCallback(async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || sending || !activeConv || !okchatToken) return;
        setInput('');
        const optimistic = {
            id: `opt_${Date.now()}`,
            content: text,
            sender: { nickname: user?.nickname },
            is_own: true,
            created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, optimistic]);
        setSending(true);
        try {
            await api.post('/okchat/send', {
                conversation_id: activeConv.id,
                content: text,
                okchat_token: okchatToken,
            });
        } catch (err) {
            if (err?.response?.status === 401) { handleAuthExpired(); }
            // optimistic stays; real delivery may have failed silently
        } finally {
            setSending(false);
        }
    }, [input, sending, activeConv, okchatToken, user, handleAuthExpired]);

    if (!open) return null;

    const MODAL_W = Math.min(860, window.innerWidth - 32);
    const MODAL_H = Math.min(640, window.innerHeight - 64);
    const SIDEBAR = 260;

    return (
        <>
            {/* ── Backdrop ── */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 11000,
                    background: 'rgba(0,0,0,0.45)',
                    backdropFilter: 'blur(2px)',
                    animation: 'fadeIn 0.15s ease',
                }}
            />

            {/* ── Modal ── */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 11001,
                    width: MODAL_W, height: MODAL_H,
                    maxWidth: 'calc(100vw - 32px)',
                    maxHeight: 'calc(100vh - 64px)',
                    background: '#fff',
                    borderRadius: 20,
                    boxShadow: '0 20px 80px rgba(0,0,0,0.35)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    animation: 'slideUp 0.2s ease',
                }}
            >
                {/* ── Header ── */}
                <div style={{
                    background: `linear-gradient(135deg, ${ACCENT}, #01579B)`,
                    color: '#fff', padding: '14px 16px',
                    display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
                }}>
                    <div style={{ width: 36, height: 36, borderRadius: 12, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <MessageSquare size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>OkChat</div>
                        <div style={{ fontSize: 11, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {status === 'ready' ? (
                                <><Wifi size={11} /> Mensajería instantánea · OkTeloVendo</>
                            ) : (
                                <><WifiOff size={11} /> Conectando…</>
                            )}
                        </div>
                    </div>
                    {/* Action buttons */}
                    <button
                        onClick={loadConvs}
                        title="Actualizar"
                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}
                    >
                        <RefreshCw size={14} />
                    </button>
                    <button
                        onClick={() => window.open(OKCHAT_BASE, '_blank', 'noopener')}
                        title="Abrir en nueva pestaña"
                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}
                    >
                        <ExternalLink size={14} />
                    </button>
                    {/* ← CLOSE BUTTON — always visible */}
                    <button
                        onClick={onClose}
                        title="Cerrar"
                        style={{
                            background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 8,
                            padding: '6px', cursor: 'pointer', color: '#fff',
                            display: 'flex', alignItems: 'center',
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div style={{ flex: 1, display: 'flex', overflowY: 'hidden' }}>

                    {/* Loading state */}
                    {status === 'loading' && (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 16, background: `linear-gradient(135deg, ${ACCENT}, #01579B)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(2,136,209,0.3)' }}>
                                <MessageSquare size={24} color="#fff" />
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>Conectando con OkChat</div>
                                <div style={{ fontSize: 13, color: '#6b7280' }}>Autenticando tu cuenta…</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                {[0, 1, 2].map(i => (
                                    <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: ACCENT, animation: `pulseDot 1.4s ${i * 0.2}s ease-in-out infinite`, opacity: 0.5 }} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* No OkChat account — show iframe fallback */}
                    {status === 'no_account' && (
                        <IframeFallback onClose={onClose} />
                    )}

                    {/* Ready — show native conversation UI */}
                    {status === 'ready' && (
                        <>
                            {/* Sidebar */}
                            <div style={{ width: SIDEBAR, borderRight: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'hidden', background: '#fff' }}>
                                <ConvList
                                    convs={convs}
                                    activeId={activeConv?.id}
                                    onSelect={openConv}
                                    onNew={() => window.open(OKCHAT_BASE, '_blank', 'noopener')}
                                    search={search}
                                    setSearch={setSearch}
                                    loading={loadingConvs}
                                />
                            </div>

                            {/* Chat area */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
                                {activeConv ? (
                                    <ChatRoom
                                        conv={activeConv}
                                        messages={messages}
                                        myNickname={user?.nickname}
                                        onBack={() => setActiveConv(null)}
                                        input={input}
                                        setInput={setInput}
                                        onSend={sendMsg}
                                        sending={sending}
                                        loadingMsgs={loadingMsgs}
                                    />
                                ) : (
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14, color: '#9ca3af', background: '#fafafa' }}>
                                        <MessageSquare size={44} style={{ opacity: 0.25 }} />
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                                                Selecciona una conversación
                                            </div>
                                            <div style={{ fontSize: 13 }}>
                                                Elige un chat de la lista o inicia uno nuevo
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => window.open(OKCHAT_BASE, '_blank', 'noopener')}
                                            style={{
                                                background: ACCENT, color: '#fff', border: 'none',
                                                borderRadius: 10, padding: '9px 20px', fontSize: 13,
                                                fontWeight: 600, cursor: 'pointer', display: 'flex',
                                                alignItems: 'center', gap: 6,
                                            }}
                                        >
                                            <Plus size={14} /> Nueva conversación
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
                @keyframes slideUp { from { transform: translate(-50%,-48%) scale(0.97); opacity:0 } to { transform: translate(-50%,-50%) scale(1); opacity:1 } }
                @keyframes bounce  { 0%,80%,100% { transform: translateY(0); opacity:0.5 } 40% { transform: translateY(-4px); opacity:1 } }
                @keyframes pulseDot { 0%,80%,100% { transform:scale(0.8); opacity:0.4 } 40% { transform:scale(1.2); opacity:1 } }
                @keyframes spin    { to { transform: rotate(360deg) } }
            `}</style>
        </>
    );
}
